import { constructApiUrl, sendRequest } from "../api/useFetch";

/**
 * Interface for offer acceptance request to the chauffeur offers endpoint
 *
 * Based on Go backend actionOffersRequest:
 * - ChauffeurUUID: header=blacklane-user-id;required
 * - UserRoles: header=blacklane-user-roles;required
 * - UserAgent: header=X-User-Agent (optional)
 * - Action: body (IncomingOfferAction)
 */
export interface AuctionBidRequest {
  action: string; // Action type (e.g., "accept")
  offer_id: string; // Offer UUID
  price_in_cents: number; // Price in cents (will be converted to float)
  chauffeur_uuid: string; // Chauffeur UUID (goes in header)
  roles: string[]; // User roles (goes in header)
}

/**
 * Ride information in the response
 */
export interface RideResponse {
  id: string;
  service_category: string;
}

/**
 * Interface for offer acceptance response from the chauffeur offers endpoint
 *
 * Based on Go backend offerActionResponse:
 * - Rides: array of rideResponse
 */
export interface AuctionBidResponse {
  rides: RideResponse[];
}

/**
 * Error response from auctions service
 */
export interface AuctionBidError {
  code: string;
  message: string;
  details?: any;
}

/**
 * Accept an offer through the chauffeur offers endpoint
 *
 * Endpoint: POST /chauffeur/offers
 * Headers: blacklane-user-id, blacklane-user-roles, X-User-Agent
 * Body: { action, id, price }
 *
 * @param request - The offer acceptance request
 * @returns Promise resolving to the offer acceptance response
 * @throws HttpResponseError if the request fails
 */
export async function acceptAuctionBid(
  request: AuctionBidRequest,
): Promise<AuctionBidResponse> {
  const path = "/chauffeur/offers";
  const url = constructApiUrl(path);

  // Convert price from cents to float (dollars/euros)
  const priceInFloat = request.price_in_cents / 100;

  // Prepare request body
  const body = {
    action: request.action,
    id: request.offer_id,
    price: priceInFloat,
  };

  // Prepare custom headers
  // Filter out "on_demand" role as it's not needed by the backend
  const filteredRoles = request.roles.filter((role) => role !== "on_demand");

  const headers: Record<string, string> = {
    "blacklane-user-id": request.chauffeur_uuid,
    "blacklane-user-roles": filteredRoles.join(","),
  };

  // Add User-Agent header if available
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    headers["X-User-Agent"] = navigator.userAgent;
  }

  // sendRequest handles authentication (Bearer token) and error responses
  const response = await sendRequest(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers,
  });

  const data = await response.json();

  // Check for error in response body (backend may return 200 with error)
  // Check for explicit error or missing expected success field
  if (data.error || !data.rides) {
    const errorCode = data.code || data.error?.code || data.error;
    const errorMessage = data.message || data.error?.message || "Unknown error";

    // Create a custom error that mimics HttpResponseError structure
    const error = new Error(errorMessage) as Error & {
      status: number;
      body: AuctionBidError;
    };
    error.status = data.status || 422; // Use backend status if available, fallback to 422
    error.body = {
      code: errorCode,
      message: errorMessage,
      details: data.details || data.error?.details,
    };
    throw error;
  }

  return data as AuctionBidResponse;
}
